import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { offlineStorage, OfflineArticle, SyncAction } from '../services/offlineStorage';

interface OfflineContextType {
  isOffline: boolean;
  offlineCount: number;
  syncQueueCount: number;
  isSyncing: boolean;
  savedArticles: Set<string>;
  saveArticleOffline: (article: OfflineArticle) => Promise<void>;
  removeArticleOffline: (articleId: string) => Promise<void>;
  isArticleSaved: (articleId: string) => boolean;
  getOfflineArticles: () => Promise<OfflineArticle[]>;
  clearAllOffline: () => Promise<void>;
  syncPendingActions: () => Promise<void>;
  refreshOfflineStatus: () => Promise<void>;
  getStorageInfo: () => Promise<{
    articleCount: number;
    syncQueueCount: number;
    estimatedSize: number;
  }>;
}

const OfflineContext = createContext<OfflineContextType | undefined>(undefined);

export const OfflineProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineCount, setOfflineCount] = useState(0);
  const [syncQueueCount, setSyncQueueCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [savedArticles, setSavedArticles] = useState<Set<string>>(new Set());

  // 监听网络状态
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      // 自动同步待处理动作
      syncPendingActions();
    };

    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // 初始化：加载离线文章列表
  useEffect(() => {
    const initOfflineData = async () => {
      try {
        await offlineStorage.init();
        await refreshOfflineStatus();
      } catch (error) {
        console.error('Failed to initialize offline storage:', error);
      }
    };

    initOfflineData();
  }, []);

  /**
   * 刷新离线状态
   */
  const refreshOfflineStatus = useCallback(async () => {
    try {
      const [count, articles, syncActions] = await Promise.all([
        offlineStorage.getOfflineCount(),
        offlineStorage.getAllArticles(),
        offlineStorage.getSyncActions(),
      ]);

      setOfflineCount(count);
      setSyncQueueCount(syncActions.length);
      setSavedArticles(new Set(articles.map((a) => a.id)));
    } catch (error) {
      console.error('Failed to refresh offline status:', error);
    }
  }, []);

  /**
   * 保存文章到离线存储
   */
  const saveArticleOffline = useCallback(async (article: OfflineArticle) => {
    try {
      await offlineStorage.saveArticle(article);
      setSavedArticles((prev) => new Set(prev).add(article.id));
      setOfflineCount((prev) => prev + 1);

      // 通知 Service Worker 缓存文章
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_ARTICLE',
          article: article,
        });
      }
    } catch (error) {
      console.error('Failed to save article offline:', error);
      throw error;
    }
  }, []);

  /**
   * 从离线存储移除文章
   */
  const removeArticleOffline = useCallback(async (articleId: string) => {
    try {
      await offlineStorage.deleteArticle(articleId);
      setSavedArticles((prev) => {
        const next = new Set(prev);
        next.delete(articleId);
        return next;
      });
      setOfflineCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to remove article offline:', error);
      throw error;
    }
  }, []);

  /**
   * 检查文章是否已保存
   */
  const isArticleSaved = useCallback(
    (articleId: string): boolean => {
      return savedArticles.has(articleId);
    },
    [savedArticles],
  );

  /**
   * 获取所有离线文章
   */
  const getOfflineArticles = useCallback(async (): Promise<OfflineArticle[]> => {
    try {
      const articles = await offlineStorage.getAllArticles();
      // 按保存时间倒序
      return articles.sort(
        (a, b) => new Date(b.saved_at).getTime() - new Date(a.saved_at).getTime(),
      );
    } catch (error) {
      console.error('Failed to get offline articles:', error);
      return [];
    }
  }, []);

  /**
   * 清空所有离线文章
   */
  const clearAllOffline = useCallback(async () => {
    try {
      await offlineStorage.clearAll();
      setSavedArticles(new Set());
      setOfflineCount(0);
    } catch (error) {
      console.error('Failed to clear offline articles:', error);
      throw error;
    }
  }, []);

  /**
   * 同步待处理动作到服务器
   */
  const syncPendingActions = useCallback(async () => {
    if (isOffline || isSyncing) {
      return;
    }

    try {
      setIsSyncing(true);
      const actions = await offlineStorage.getSyncActions();

      if (actions.length === 0) {
        return;
      }

      // 执行同步（这里需要根据实际API实现）
      for (const action of actions) {
        try {
          switch (action.type) {
            case 'mark_read':
              // 调用标记已读API
              // await articleApi.markAsRead(action.article_id);
              break;
            case 'mark_favorite':
              // 调用收藏API
              // await articleApi.toggleFavorite(action.article_id);
              break;
          }

          // 同步成功后删除动作
          if (action.id) {
            await offlineStorage.deleteSyncAction(action.id);
          }
        } catch (error) {
          console.error('Failed to sync action:', action, error);
          // 继续同步其他动作
        }
      }

      // 刷新同步队列计数
      await refreshOfflineStatus();
    } catch (error) {
      console.error('Failed to sync pending actions:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [isOffline, isSyncing, refreshOfflineStatus]);

  /**
   * 获取存储使用情况
   */
  const getStorageInfo = useCallback(async () => {
    return await offlineStorage.getStorageInfo();
  }, []);

  const value: OfflineContextType = {
    isOffline,
    offlineCount,
    syncQueueCount,
    isSyncing,
    savedArticles,
    saveArticleOffline,
    removeArticleOffline,
    isArticleSaved,
    getOfflineArticles,
    clearAllOffline,
    syncPendingActions,
    refreshOfflineStatus,
    getStorageInfo,
  };

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
};

export const useOffline = (): OfflineContextType => {
  const context = useContext(OfflineContext);
  if (context === undefined) {
    throw new Error('useOffline must be used within an OfflineProvider');
  }
  return context;
};
